import { dropTask } from 'ember-concurrency';
import { eq, not } from 'tio-ui/utilities';
import { modifier } from 'ember-modifier';
import { on } from '@ember/modifier';
import { PhShieldCheck, PhDeviceMobile, PhArrowClockwise } from 'ember-phosphor-icons';
import { sendPhoneVerificationRequest, verifyPhoneRequest } from 'tio-common/requests/phone-number';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import AlertBanner from 'tio-ui/components/alert-banner';
import Button from 'tio-ui/components/button';
import Component from '@glimmer/component';
import formatPhone from 'tio-common/helpers/format-phone';
import Heading from 'tio-ui/components/heading';
import Input from 'tio-ui/components/input';
import Modal from 'tio-ui/components/modal';
import type IntlService from 'ember-intl/services/intl';
import type StoreService from 'ember-data/store';

type VerifyStep = 'send' | 'enter-code';

export interface MfaVerificationSignature {
  Args: {
    show: boolean;
    phoneNumber: string;
    /** If provided, verifies a new phone number (phone-change flow) */
    newPhoneNumber?: string;
    /** If true, automatically sends verification code when modal opens */
    autoSend?: boolean;
    /** Initial step when modal opens. Allows reopening at 'enter-code' if a code was already sent. */
    initialStep?: VerifyStep;
    onVerified: () => void;
    onCancel: () => void;
    /** Called when a verification code is successfully sent */
    onCodeSent?: () => void;
  };
}

export default class MfaVerification extends Component<MfaVerificationSignature> {
  @service declare intl: IntlService;
  @service declare store: StoreService;

  @tracked private _stepOverride?: VerifyStep;
  @tracked code = '';
  @tracked errorMessage = '';
  @tracked successMessage = '';

  get step(): VerifyStep {
    return this._stepOverride ?? this.args.initialStep ?? 'send';
  }

  get isCodeStep() {
    return this.step === 'enter-code';
  }

  get displayPhone() {
    return this.args.newPhoneNumber || this.args.phoneNumber;
  }

  get descriptionKey() {
    return this.args.newPhoneNumber
      ? 'profile.mfa.verification.description_change'
      : 'profile.mfa.verification.description';
  }

  triggerAutoSend = modifier(() => {
    if (this.args.autoSend && this.step === 'send') {
      this.sendCode.perform();
    }
  });

  handleCodeChange = (value: string) => {
    this.code = value.replace(/\D/g, '').slice(0, 6);
    if (this.errorMessage) {
      this.errorMessage = '';
    }
  };

  sendCode = dropTask(async () => {
    this.errorMessage = '';
    this.successMessage = '';

    try {
      await this.store.request(sendPhoneVerificationRequest(this.args.newPhoneNumber));

      this._stepOverride = 'enter-code';
      this.args.onCodeSent?.();
    } catch {
      this.errorMessage = this.intl.t('profile.mfa.verification.error_send_failed');
    }
  });

  verifyCode = dropTask(async () => {
    this.errorMessage = '';

    if (!this.code || this.code.length < 6) {
      this.errorMessage = this.intl.t('profile.mfa.verification.error_invalid_code');
      return;
    }

    try {
      await this.store.request(verifyPhoneRequest(this.code, this.args.newPhoneNumber));

      this.args.onVerified();
      this.resetState();
    } catch {
      this.errorMessage = this.intl.t('profile.mfa.verification.error_verify_failed');
    }
  });

  resendCode = dropTask(async () => {
    this.errorMessage = '';
    this.successMessage = '';

    try {
      await this.store.request(sendPhoneVerificationRequest(this.args.newPhoneNumber));
      this.successMessage = this.intl.t('profile.mfa.verification.resend_success');
      this.args.onCodeSent?.();
    } catch {
      this.errorMessage = this.intl.t('profile.mfa.verification.error_send_failed');
    }
  });

  handleCancel = () => {
    this.resetState();
    this.args.onCancel();
  };

  handleSubmit = (event: Event) => {
    event.preventDefault();
    this.verifyCode.perform();
  };

  private resetState() {
    this._stepOverride = undefined;
    this.code = '';
    this.errorMessage = '';
    this.successMessage = '';
  }

  <template>
    <Modal
      @isOpen={{@show}}
      @onClose={{this.handleCancel}}
      @closeOnOutsideClick={{not this.isCodeStep}}
      @closeOnEscapeKey={{not this.isCodeStep}}
      @size="md"
    >
      <:header>
        <div class="tio-row">
          <div class="w-10 h-10 rounded-xl tio-bg-brand-soft tio-row-center">
            <PhShieldCheck class="tio-fg-brand" @size={{20}} @weight="fill" />
          </div>
          <Heading @level={{2}} @size="lg">
            {{t "profile.mfa.verification.title"}}
          </Heading>
        </div>
      </:header>
      <:body>
        <div class="space-y-4">
          {{#if this.errorMessage}}
            <AlertBanner @intent="warning" data-test-verify-error>
              {{this.errorMessage}}
            </AlertBanner>
          {{/if}}

          {{#if this.successMessage}}
            <AlertBanner @intent="success" data-test-verify-success>
              {{this.successMessage}}
            </AlertBanner>
          {{/if}}

          {{#if (eq this.step "send")}}
            <div {{this.triggerAutoSend}} class="tio-row-top">
              <PhDeviceMobile class="tio-fg-disabled shrink-0 mt-0.5" @size={{20}} />
              <p class="text-sm tio-fg-secondary">
                {{#if @autoSend}}
                  {{t "profile.mfa.verification.sending"}}
                {{else}}
                  {{t this.descriptionKey phoneNumber=(formatPhone this.displayPhone)}}
                {{/if}}
              </p>
            </div>
          {{else}}
            <p class="text-sm tio-fg-secondary">
              {{t "profile.mfa.verification.enter_code"}}
            </p>

            <form {{on "submit" this.handleSubmit}}>
              <div class="space-y-4">
                <Input
                  @label={{t "profile.mfa.verification.code_label"}}
                  @value={{this.code}}
                  @onChange={{this.handleCodeChange}}
                  @placeholder={{t "profile.mfa.verification.code_placeholder"}}
                  @inputmode="numeric"
                  @autocomplete="one-time-code"
                  @maxlength={{6}}
                  @leadingIcon={{PhShieldCheck}}
                  data-test-verify-code-input
                />

                <div class="tio-row">
                  <Button
                    @type="submit"
                    @intent="primary"
                    @isRunning={{this.verifyCode.isRunning}}
                    data-test-verify-submit
                  >
                    {{t "profile.mfa.verification.verify"}}
                  </Button>

                  <Button
                    @appearance="minimal"
                    @intent="default"
                    @isRunning={{this.resendCode.isRunning}}
                    @icon={{PhArrowClockwise}}
                    {{on "click" this.resendCode.perform}}
                    data-test-verify-resend
                  >
                    {{t "profile.mfa.verification.resend"}}
                  </Button>
                </div>
              </div>
            </form>
          {{/if}}
        </div>
      </:body>
      <:footer>
        <div class="tio-flex-end gap-3">
          <Button
            @appearance="outlined"
            @intent="default"
            {{on "click" this.handleCancel}}
            data-test-verify-cancel
          >
            {{t "profile.mfa.verification.cancel"}}
          </Button>

          {{#unless @autoSend}}
            {{#if (eq this.step "send")}}
              <Button
                @intent="primary"
                @isRunning={{this.sendCode.isRunning}}
                {{on "click" this.sendCode.perform}}
                data-test-verify-send
              >
                {{t "profile.mfa.verification.send_code"}}
              </Button>
            {{/if}}
          {{/unless}}
        </div>
      </:footer>
    </Modal>
  </template>
}
